import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { childrenService, activityService } from '../services/api';
import { Child } from '../types';
import { getBadge, formatDate } from '../data/badges';
import { getBadgeImage, getBadgeName } from '../data/badgeImages';
import BadgeGallery from './dashboard/BadgeGallery';
import TitleUnlockCelebration from './dashboard/TitleUnlockCelebration';
import AvatarSelector from './dashboard/AvatarSelector';
import { getUnlockedTitles, getHighestTitle, getTitleColor, BOOK_TITLES } from '../data/bookTitles';
import { getAvatarById, getRandomAvatar } from '../data/avatars';

interface Book {
    book_number: number;
    is_unlocked: boolean;
    activities: BookActivity[];
}

interface BookActivity {
    number: number;
    title: string;
    description?: string;
    submitted: boolean;
    status?: 'pending' | 'approved' | 'rejected';
    reviewed_at?: string;
    description_es?: string;
    description_en?: string;
}

interface BookOverview {
    books: Book[];
}

/**
 * Página de actividades para niños
 * Diseño con layout de 3 columnas: ficha niño, libros pills, descripción actividades
 */
const ActivityPage: React.FC = () => {
    const { childId } = useParams<{ childId: string }>();
    const navigate = useNavigate();
    const { language, setLanguage } = useLanguage();
    const t = (es: string, en: string) => language === 'es' ? es : en;
    
    const [booksOverview, setBooksOverview] = useState<BookOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [childLoading, setChildLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [child, setChild] = useState<Child | null>(null);
    
    // Estado para controlar la vista actual
    const [currentView, setCurrentView] = useState<'books' | 'experiments'>('books');
    
    // Estados para el modal de código
    const [codeModalOpen, setCodeModalOpen] = useState(false);
    const [selectedBook, setSelectedBook] = useState<number | null>(null);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState<string | null>(null);
    const [unlocking, setUnlocking] = useState(false);
    
    // Estados para la actividad seleccionada
    const [selectedActivity, setSelectedActivity] = useState<BookActivity | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    
    // Estados para subida de archivo
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    
    // Estado para modal de felicitación
    const [congratsModalOpen, setCongratsModalOpen] = useState(false);
    
    // Estado para descarga de documentación
    const [downloadingDoc, setDownloadingDoc] = useState(false);
    
    // Estado para mostrar galería de insignias
    const [showBadgeGallery, setShowBadgeGallery] = useState(false);
    
    // Estado para celebración de títulos
    const [titleCelebration, setTitleCelebration] = useState<{
        isVisible: boolean;
        bookNumber: number;
        title: string;
    }>({
        isVisible: false,
        bookNumber: 0,
        title: ''
    });
    
    // Estado para tracking de títulos previos
    const [previousUnlockedTitles, setPreviousUnlockedTitles] = useState<string[]>([]);
    
    // Estado para selector de avatares
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);
    const [selectedAvatarId, setSelectedAvatarId] = useState<string>('');

    useEffect(() => {
        const loadData = async () => {
            await loadBooksOverview();
            await loadChildData();
        };
        loadData();
    }, [childId]);

    // Detectar nuevos títulos desbloqueados
    useEffect(() => {
        if (booksOverview && booksOverview.books) {
            const currentUnlockedTitles = getUnlockedTitles(booksOverview);
            
            // Detectar si hay nuevos títulos desbloqueados
            if (previousUnlockedTitles.length > 0) {
                const newTitles = currentUnlockedTitles.filter(title => 
                    !previousUnlockedTitles.includes(title)
                );
                
                if (newTitles.length > 0) {
                    // Mostrar celebración para el título más reciente
                    const newTitle = newTitles[newTitles.length - 1];
                    const bookNumber = Object.keys(BOOK_TITLES).find(key => 
                        BOOK_TITLES[parseInt(key)] === newTitle
                    );
                    
                    if (bookNumber) {
                        setTitleCelebration({
                            isVisible: true,
                            bookNumber: parseInt(bookNumber),
                            title: newTitle
                        });
                    }
                }
            }
            
            // Actualizar la lista de títulos previos
            setPreviousUnlockedTitles(currentUnlockedTitles);
        }
    }, [booksOverview]);

    // Asignar avatar aleatorio si no hay uno seleccionado
    useEffect(() => {
        if (!selectedAvatarId && child) {
            const randomAvatar = getRandomAvatar();
            setSelectedAvatarId(randomAvatar.id);
        }
    }, [child, selectedAvatarId]);

    const handleCloseTitleCelebration = () => {
        setTitleCelebration(prev => ({
            ...prev,
            isVisible: false
        }));
    };

    const handleAvatarSelect = (avatarId: string) => {
        setSelectedAvatarId(avatarId);
        // Aquí se podría guardar el avatar seleccionado en la base de datos
        console.log('Avatar seleccionado:', avatarId);
    };

    const handleShowAvatarSelector = () => {
        setShowAvatarSelector(true);
    };

    const handleCloseAvatarSelector = () => {
        setShowAvatarSelector(false);
    };

    const loadBooksOverview = async () => {
        try {
            setLoading(true);
            const data = await activityService.getBooksOverview(parseInt(childId!));
            setBooksOverview(data);
        } catch (err) {
            setError(t('Error al cargar los libros', 'Error loading books'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadChildData = async () => {
        try {
            setChildLoading(true);
            
            // Primero intentar cargar desde localStorage para mostrar datos rápidamente
            const dashboardData = localStorage.getItem('dashboardData');
            if (dashboardData) {
                const data = JSON.parse(dashboardData);
                const childData = data.children?.find((c: Child) => c.id === parseInt(childId!));
                if (childData) {
                    setChild(childData);
                }
            }
            
            // Luego hacer una llamada a la API para obtener datos actualizados
            try {
                const childData = await childrenService.getChild(parseInt(childId!));
                setChild(childData);
                
                // Actualizar el localStorage con los datos actualizados
                if (dashboardData) {
                    const data = JSON.parse(dashboardData);
                    const updatedChildren = data.children?.map((c: Child) => 
                        c.id === parseInt(childId!) ? childData : c
                    );
                    const updatedData = { ...data, children: updatedChildren };
                    localStorage.setItem('dashboardData', JSON.stringify(updatedData));
                }
            } catch (apiError) {
                console.warn('No se pudo cargar datos actualizados del niño:', apiError);
                // Si falla la API, mantener los datos del localStorage
            }
        } catch (error) {
            console.error('Error al cargar datos del niño:', error);
        } finally {
            setChildLoading(false);
        }
    };

    const calculateAge = (birthDate: string): number => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };

    const handleBookClick = (bookNumber: number, isUnlocked: boolean) => {
        if (isUnlocked) {
            setSelectedBook(bookNumber);
            setCurrentView('experiments');
        } else {
            setSelectedBook(bookNumber);
            setCodeModalOpen(true);
        }
    };

    const handleUnlockBook = async () => {
        if (!code.trim()) {
            setCodeError(t('Por favor ingresa el código', 'Please enter the code'));
            return;
        }

        try {
            setUnlocking(true);
            setCodeError(null);
            
            const response = await activityService.validateProductCode(code, parseInt(childId!));
            
            if (response.success) {
                setCodeModalOpen(false);
                setCode('');
                setSelectedBook(null);
                await loadBooksOverview();
            } else {
                setCodeError(response.message);
            }
        } catch (err: any) {
            console.error('Error validating code:', err);
            let errorMessage = t('Error al validar el código', 'Error validating code');
            
            if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setCodeError(errorMessage);
        } finally {
            setUnlocking(false);
        }
    };

    const handleActivityClick = (activity: BookActivity) => {
        console.log('Actividad seleccionada:', activity);
        // Resetear estados de subida cuando se selecciona una nueva actividad
        setUploadSuccess(false);
        setSelectedFile(null);
        
        if (activity.status === 'approved') {
            // Permitir resubir actividades aprobadas
            setSelectedActivity(activity);
            return;
        }
        
        if (activity.submitted && activity.status === 'pending') {
            // Mostrar la actividad pero con mensaje de que ya fue enviada
            setSelectedActivity(activity);
            return;
        }
        
        if (activity.submitted && activity.status === 'rejected') {
            // Permitir reenviar si fue rechazada
            setSelectedActivity(activity);
            return;
        }
        
        // Actividad no enviada - permitir subir
        setSelectedActivity(activity);
    };

    const handleBackToExperiments = () => {
        setSelectedActivity(null);
        setUploadSuccess(false);
        setSelectedFile(null);
    };

    const handleBackToBooks = () => {
        setCurrentView('books');
        setSelectedBook(null);
        setSelectedActivity(null);
        setUploadSuccess(false);
        setSelectedFile(null);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadExperience = async () => {
        if (!selectedFile || !selectedActivity || !selectedBook) return;

        try {
            setUploading(true);
            
            const formData = new FormData();
            formData.append('child_id', childId!);
            formData.append('book_number', selectedBook.toString());
            formData.append('activity_number', selectedActivity.number.toString());
            formData.append('image', selectedFile);

            await activityService.uploadExperience(formData);
            setUploadSuccess(true);
            setSelectedFile(null);
            
            // Recargar datos para actualizar el estado
            await loadBooksOverview();
        } catch (err: any) {
            alert(err.response?.data?.detail || t('Error al subir la actividad', 'Error uploading activity'));
        } finally {
            setUploading(false);
        }
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    const handleDownloadDocumentation = async () => {
        if (!selectedActivity || !selectedBook) return;
        
        try {
            setDownloadingDoc(true);
            await activityService.downloadDocumentation(selectedBook, selectedActivity.number);
        } catch (err: any) {
            alert(err.response?.data?.detail || t('Error al descargar la documentación', 'Error downloading documentation'));
        } finally {
            setDownloadingDoc(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t('Cargando...', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-4">
                <div className="max-w-4xl mx-auto mt-8">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                    <button 
                        onClick={handleBackToDashboard}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        {t('Volver al Dashboard', 'Back to Dashboard')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Botón de traducción en la esquina superior derecha */}
            <div className="fixed top-4 right-4 z-50">
                <button
                    onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                    className="bg-white bg-opacity-90 backdrop-blur-sm border-2 border-white shadow-lg rounded-full p-3 hover:bg-opacity-100 hover:scale-110 transition-all duration-300"
                >
                    <span className="text-2xl">
                        {language === 'es' ? '🇺🇸' : '🇪🇸'}
                    </span>
                </button>
            </div>

            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                            <button 
                                onClick={handleBackToDashboard}
                                className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                            >
                                ← {t('Volver', 'Back')}
                            </button>
                            <h1 className="text-2xl font-bold text-gray-800">
                                {t('Biblioteca de Actividades', 'Activity Library')}
                            </h1>
                        </div>
                        <div>
                            <button
                                onClick={() => setShowBadgeGallery(!showBadgeGallery)}
                                className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full font-medium hover:from-yellow-500 hover:to-orange-500 transition-all duration-200 transform hover:scale-105 shadow-lg text-sm"
                            >
                                🏆 {showBadgeGallery ? t('Ocultar', 'Hide') : t('Ver', 'View')} Insignias
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Galería de insignias */}
            {showBadgeGallery && (
                <div className="max-w-7xl mx-auto p-4 mb-6">
                    <BadgeGallery 
                        booksOverview={booksOverview}
                        childId={parseInt(childId!)}
                    />
                </div>
            )}

            <div className="max-w-7xl mx-auto p-4 w-full overflow-x-hidden mobile-scroll-container">
                {/* Layout principal con 3 columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 w-full">
                    
                    {/* Columna izquierda: Ficha del niño + Libros */}
                    <div className="lg:col-span-4">
                        {/* Ficha del niño */}
                        <div className="bg-white rounded-lg shadow p-6 mb-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('Ficha del Niño', 'Child Profile')}</h2>
                            
                            {childLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">{t('Cargando datos del niño...', 'Loading child data...')}</p>
                                </div>
                            ) : child ? (
                                <div className="text-center">
                                    {/* Avatar del niño */}
                                    <div className="relative mb-4">
                                        <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-lg">
                                            {selectedAvatarId ? (
                                                <img
                                                    src={getAvatarById(selectedAvatarId)?.path || ''}
                                                    alt={`Avatar de ${child.full_name}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                                                    {child.full_name.charAt(0)}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Botón para cambiar avatar */}
                                        <button
                                            onClick={handleShowAvatarSelector}
                                            className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-200 shadow-lg"
                                            title="Cambiar avatar"
                                        >
                                            ✏️
                                        </button>
                                    </div>
                                    
                                    {/* Nombre del niño */}
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{child.full_name}</h3>
                                    <p className="text-gray-600 mb-3">{calculateAge(child.birth_date)} {t('años', 'years')}</p>
                                    <p className="text-sm text-gray-500 mb-4">{child.country || t('País no especificado', 'Country not specified')}</p>
                                    
                                    {/* Títulos desbloqueados */}
                                    {booksOverview && (
                                        <div className="space-y-3">
                                            {/* Título más alto */}
                                            {(() => {
                                                const highestTitle = getHighestTitle(booksOverview);
                                                if (highestTitle) {
                                                    const bookNumber = Object.keys(BOOK_TITLES).find(key => 
                                                        BOOK_TITLES[parseInt(key)] === highestTitle
                                                    );
                                                    const colorClass = bookNumber ? getTitleColor(parseInt(bookNumber)) : "from-gray-500 to-gray-600";
                                                    
                                                    return (
                                                        <div className={`bg-gradient-to-r ${colorClass} text-white rounded-lg p-4 shadow-lg transform hover:scale-105 transition-all duration-200 border-2 border-white`}>
                                                            <div className="text-sm font-medium mb-2 opacity-90">
                                                                {t('Título Actual', 'Current Title')}
                                                            </div>
                                                            <div className="text-xl font-bold mb-2">
                                                                👑 {highestTitle}
                                                            </div>
                                                            <div className="text-xs opacity-75">
                                                                {t('Libro', 'Book')} {bookNumber} {t('completado', 'completed')}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            
                                            {/* Lista de todos los títulos desbloqueados */}
                                            {(() => {
                                                const unlockedTitles = getUnlockedTitles(booksOverview);
                                                if (unlockedTitles.length > 1) {
                                                    return (
                                                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 border border-purple-200">
                                                            <div className="text-sm font-medium text-purple-700 mb-3 text-center">
                                                                🏆 {t('Títulos Desbloqueados', 'Unlocked Titles')} ({unlockedTitles.length})
                                                            </div>
                                                            <div className="grid grid-cols-1 gap-2">
                                                                {unlockedTitles.slice(0, 4).map((title, index) => {
                                                                    const bookNumber = Object.keys(BOOK_TITLES).find(key => 
                                                                        BOOK_TITLES[parseInt(key)] === title
                                                                    );
                                                                    const colorClass = bookNumber ? getTitleColor(parseInt(bookNumber)) : "from-gray-400 to-gray-500";
                                                                    
                                                                    return (
                                                                        <div key={index} className={`bg-gradient-to-r ${colorClass} text-white rounded px-3 py-2 text-xs font-medium shadow-sm`}>
                                                                            {title}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {unlockedTitles.length > 4 && (
                                                                    <div className="text-xs text-purple-600 text-center bg-white rounded px-2 py-1 border border-purple-200">
                                                                        +{unlockedTitles.length - 4} {t('más', 'more')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            
                                            {/* Progreso general */}
                                            {(() => {
                                                const totalBooks = booksOverview.books.length;
                                                const completedBooks = booksOverview.books.filter((book: any) => 
                                                    book.is_unlocked && book.activities.every((activity: any) => 
                                                        activity.status === 'approved'
                                                    )
                                                ).length;
                                                
                                                if (totalBooks > 0) {
                                                    const percentage = Math.round((completedBooks / totalBooks) * 100);
                                                    return (
                                                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                                                            <div className="text-sm font-medium text-green-700 mb-2 text-center">
                                                                📚 {t('Progreso General', 'Overall Progress')}
                                                            </div>
                                                            <div className="w-full bg-green-200 rounded-full h-3 mb-2">
                                                                <div 
                                                                    className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full transition-all duration-500 shadow-sm"
                                                                    style={{ width: `${percentage}%` }}
                                                                ></div>
                                                            </div>
                                                            <div className="text-xs text-green-700 text-center">
                                                                {completedBooks}/{totalBooks} {t('libros completados', 'books completed')} ({percentage}%)
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-4xl mb-4">👤</div>
                                    <p className="text-gray-600">{t('No se pudieron cargar los datos del niño', 'Could not load child data')}</p>
                                </div>
                            )}
                        </div>

                        {/* Libros seleccionables */}
                        {currentView === 'books' ? (
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4">{t('Libros Disponibles', 'Available Books')}</h2>
                                
                                <div className="space-y-3">
                                    {booksOverview?.books.map((book) => (
                                        <div 
                                            key={book.book_number}
                                            onClick={() => handleBookClick(book.book_number, book.is_unlocked)}
                                            className={`rounded-lg p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
                                                book.is_unlocked 
                                                    ? 'bg-green-500 text-white border-green-600 hover:bg-green-600' 
                                                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="text-2xl mr-3">
                                                        {book.is_unlocked ? '🔓' : '🔒'}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-semibold text-lg">
                                                            {t('Libro', 'Book')} {book.book_number}
                                                        </h3>
                                                        <p className="text-sm opacity-90">
                                                            {book.is_unlocked 
                                                                ? `${book.activities.length} ${t('actividades disponibles', 'activities available')}`
                                                                : t('Ingresa código para desbloquear', 'Enter code to unlock')
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                {book.is_unlocked && (
                                                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm font-medium">
                                                        {book.activities.filter(a => a.status === 'approved').length}/{book.activities.length}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // Vista de actividades con pill de volver atrás
                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4">{t('Actividades', 'Activities')} - {t('Libro', 'Book')} {selectedBook}</h2>
                                
                                <div className="space-y-3">
                                    {booksOverview?.books
                                        .find(b => b.book_number === selectedBook)
                                        ?.activities.map((activity) => (
                                            <div 
                                                key={`${selectedBook}-${activity.number}`}
                                                onClick={() => handleActivityClick(activity)}
                                                className={`rounded-lg p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
                                                    activity.status === 'approved' 
                                                        ? 'bg-green-500 text-white border-green-600' 
                                                        : activity.status === 'pending'
                                                        ? 'bg-yellow-500 text-white border-yellow-600'
                                                        : activity.status === 'rejected'
                                                        ? 'bg-red-500 text-white border-red-600'
                                                        : 'bg-gray-300 text-gray-600 border-gray-400 hover:bg-gray-400'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <div className="text-2xl mr-3">
                                                            {activity.status === 'approved' ? '🎉' : 
                                                             activity.status === 'pending' ? '⏳' : 
                                                             activity.status === 'rejected' ? '❌' : '🔬'}
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-lg">
                                                                {t('Actividad', 'Activity')} {activity.number}
                                                            </h3>
                                                            <p className="text-sm opacity-90">{activity.title}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex items-center space-x-3">
                                                        {/* Mostrar insignia si la actividad está aprobada, o etiqueta de estado si no */}
                                                        {activity.status === 'approved' && selectedBook && activity.number ? (
                                                            <div className="relative">
                                                                <img 
                                                                    src={getBadgeImage(selectedBook, activity.number)} 
                                                                    alt={`Insignia ${getBadgeName(selectedBook, activity.number)}`}
                                                                    className="w-16 h-16 rounded-full border-3 border-white shadow-xl"
                                                                    title={getBadgeName(selectedBook, activity.number)}
                                                                />
                                                                {/* Efecto de brillo dorado */}
                                                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-300/40 to-transparent rounded-full animate-pulse"></div>
                                                            </div>
                                                        ) : (
                                                            <div className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm font-medium">
                                                                {activity.status === 'pending' ? t('En Revisión', 'Under Review') :
                                                                 activity.status === 'rejected' ? t('Rechazado', 'Rejected') :
                                                                 t('Pendiente', 'Pending')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    
                                    {/* Pill para volver atrás */}
                                    <div 
                                        onClick={handleBackToBooks}
                                        className="rounded-lg p-4 cursor-pointer transition-all hover:shadow-md border-2 border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    >
                                        <div className="flex items-center justify-center">
                                            <div className="text-2xl mr-3">←</div>
                                            <div>
                                                <h3 className="font-semibold text-lg">{t('Volver a Libros', 'Back to Books')}</h3>
                                                <p className="text-sm opacity-90">{t('Ver todos los libros disponibles', 'View all available books')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Columna central: Imagen "Vamos a comenzar" o actividad seleccionada */}
                    <div className="lg:col-span-5">
                        {currentView === 'books' ? (
                            // Imagen "Vamos a comenzar"
                            <div className="bg-white rounded-lg shadow p-8 flex flex-col items-center justify-center min-h-96">
                                <div className="text-center">
                                    <div className="text-8xl mb-6">🚀</div>
                                    <h2 className="text-3xl font-bold text-gray-800 mb-4">{t('¡Vamos a Comenzar!', 'Let\'s Get Started!')}</h2>
                                    <p className="text-lg text-gray-600 mb-6">
                                        {t('Selecciona un libro de la izquierda para comenzar tus actividades científicas', 'Select a book from the left to start your scientific activities')}
                                    </p>
                                    <div className="bg-blue-50 rounded-lg p-6 max-w-md mx-auto">
                                        <h3 className="font-semibold text-blue-800 mb-2">{t('¿Cómo funciona?', 'How does it work?')}</h3>
                                        <ul className="text-blue-700 text-sm space-y-1">
                                            <li>• {t('Selecciona un libro desbloqueado', 'Select an unlocked book')}</li>
                                            <li>• {t('Elige una actividad para realizar', 'Choose an activity to perform')}</li>
                                            <li>• {t('Sigue las instrucciones paso a paso', 'Follow the step-by-step instructions')}</li>
                                            <li>• {t('Sube una foto de tu resultado', 'Upload a photo of your result')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ) : selectedActivity ? (
                            // Vista de actividad específica
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="mb-6">
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                        {t('Actividad', 'Activity')} {selectedActivity.number}: {selectedActivity.title}
                                    </h3>
                                </div>

                                {!uploadSuccess && selectedActivity && (!selectedActivity.submitted || selectedActivity.status === 'rejected' || selectedActivity.status === 'approved') ? (
                                    <div className="bg-gray-50 rounded-lg p-6">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-4">
                                            {selectedActivity.status === 'rejected' ? t('Reenviar Actividad', 'Resend Activity') : 
                                             selectedActivity.status === 'approved' ? t('Actualizar Actividad', 'Update Activity') : t('Subir Actividad', 'Upload Activity')}
                                        </h4>
                                        
                                        {selectedActivity.status === 'rejected' && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                                <p className="text-red-800 text-sm">
                                                    <strong>{t('Nota:', 'Note:')}</strong> {t('Esta actividad fue rechazada anteriormente. Puedes subir una nueva imagen para volver a enviarla.', 'This activity was previously rejected. You can upload a new image to resend it.')}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {selectedActivity.status === 'approved' && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                                <p className="text-green-800 text-sm">
                                                    <strong>{t('¡Excelente trabajo!', 'Excellent work!')}</strong> {t('Esta actividad ya fue aprobada. Puedes subir una nueva imagen si quieres mejorar tu trabajo o mostrar una versión actualizada.', 'This activity has already been approved. You can upload a new image if you want to improve your work or show an updated version.')}
                                                </p>
                                                <div className="mt-3 flex space-x-2">
                                                    <button
                                                        onClick={() => setCongratsModalOpen(true)}
                                                        className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                                    >
                                                        🎉 {t('Ver Insignia', 'View Badge')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Seleccionar imagen', 'Select image')}
                                                </label>
                                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleFileSelect}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
                                                    />
                                                    <button
                                                        onClick={handleUploadExperience}
                                                        disabled={!selectedFile || uploading}
                                                        className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] text-center"
                                                    >
                                                        {uploading ? t('Subiendo...', 'Uploading...') : 
                                                         selectedActivity.status === 'rejected' ? t('Reenviar', 'Resend') : 
                                                         selectedActivity.status === 'approved' ? t('Actualizar', 'Update') : t('Subir', 'Upload')}
                                                    </button>
                                                </div>
                                                {selectedFile && (
                                                    <p className="text-sm text-green-600 mt-1">
                                                        ✅ {t('Archivo seleccionado:', 'File selected:')} {selectedFile.name}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            {/* Botón de descarga de documentación para actividad 2 del libro 1 */}
                                            {selectedBook === 1 && selectedActivity.number === 2 && (
                                                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                    <div className="flex items-center mb-2">
                                                        <div className="text-yellow-600 mr-2">📋</div>
                                                        <h4 className="font-semibold text-yellow-800">{t('Documentación de Apoyo', 'Support Documentation')}</h4>
                                                    </div>
                                                    <p className="text-yellow-700 text-sm mb-3">
                                                        {t('Esta actividad requiere documentación adicional para su realización.', 'This activity requires additional documentation for its completion.')}
                                                    </p>
                                                    <button
                                                        onClick={handleDownloadDocumentation}
                                                        disabled={downloadingDoc}
                                                        className="w-full px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                                    >
                                                        {downloadingDoc ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                                {t('Descargando...', 'Downloading...')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                📥 {t('Descargar Documentación', 'Download Documentation')}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                            
                                            {/* Tarjetas de descarga para actividad 4 del libro 1 */}
                                            {selectedBook === 1 && selectedActivity.number === 4 && (
                                                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                    <div className="flex items-center mb-3">
                                                        <div className="text-green-600 mr-2">🃏</div>
                                                        <h4 className="font-semibold text-green-800">{t('Tarjetas del Experimento', 'Experiment Cards')}</h4>
                                                    </div>
                                                    <p className="text-green-700 text-sm mb-4">
                                                        {t('Descarga las tarjetas individuales o todas las tarjetas del experimento.', 'Download individual cards or all experiment cards.')}
                                                    </p>
                                                    <div className="space-y-3">
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <a 
                                                                href="/images/experimento4/tarjeta 1.png"
                                                                download="tarjeta_1_experimento4.png"
                                                                className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold py-2 px-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 text-center"
                                                            >
                                                                Tarjeta 1
                                                            </a>
                                                            <a 
                                                                href="/images/experimento4/tarjeta 2.png"
                                                                download="tarjeta_2_experimento4.png"
                                                                className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold py-2 px-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 text-center"
                                                            >
                                                                Tarjeta 2
                                                            </a>
                                                            
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <a 
                                                                href="/images/experimento4/tarjeta 3.png"
                                                                download="tarjeta_3_experimento4.png"
                                                                className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold py-2 px-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 text-center"
                                                            >
                                                                Tarjeta 3
                                                            </a>
                                                            <a 
                                                                href="/images/experimento4/tarjeta 4.png"
                                                                download="tarjeta_4_experimento4.png"
                                                                className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold py-2 px-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 text-center"
                                                            >
                                                                Tarjeta 4
                                                            </a>
                                                        </div>
                                                        <button 
                                                            onClick={() => {
                                                                const tarjetas = [
                                                                    "/images/experimento4/tarjeta 1.png",
                                                                    "/images/experimento4/tarjeta 2.png", 
                                                                    "/images/experimento4/tarjeta 3.png",
                                                                    "/images/experimento4/tarjeta 4.png"
                                                                ];
                                                                tarjetas.forEach((tarjeta, idx) => {
                                                                    const link = document.createElement('a');
                                                                    link.href = tarjeta;
                                                                    link.download = `tarjeta_${idx + 1}_experimento4.png`;
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    document.body.removeChild(link);
                                                                });
                                                            }}
                                                            className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                                                        >
                                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            {t('Descargar Todas las Tarjetas', 'Download All Cards')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Botón de descarga de documentación para actividad 7 del libro 1 */}
                                            {selectedBook === 1 && selectedActivity.number === 7 && (
                                                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                    <div className="flex items-center mb-2">
                                                        <div className="text-yellow-600 mr-2">📋</div>
                                                        <h4 className="font-semibold text-yellow-800">{t('Documentación de Apoyo', 'Support Documentation')}</h4>
                                                    </div>
                                                    <p className="text-yellow-700 text-sm mb-3">
                                                        {t('Esta actividad requiere documentación adicional para su realización.', 'This activity requires additional documentation for its completion.')}
                                                    </p>
                                                    <button
                                                        onClick={handleDownloadDocumentation}
                                                        disabled={downloadingDoc}
                                                        className="w-full px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                                    >
                                                        {downloadingDoc ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                                {t('Descargando...', 'Downloading...')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                📥 {t('Descargar Documentación', 'Download Documentation')}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : uploadSuccess ? (
                                    <div className="text-center py-8">
                                        <div className="text-6xl mb-4">✨</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                                            {t('¡Actividad Enviada!', 'Activity Sent!')}
                                        </h3>
                                        <p className="text-gray-600 mb-6">
                                            {t('Tu actividad ha sido enviada y será revisada.', 'Your activity has been sent and will be reviewed.')}
                                        </p>
                                        <button
                                            onClick={handleBackToExperiments}
                                            className="px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            {t('Volver a Actividades', 'Back to Activities')}
                                        </button>
                                    </div>
                                ) : selectedActivity && selectedActivity.submitted && selectedActivity.status === 'pending' ? (
                                    <div className="text-center py-8">
                                        <div className="text-6xl mb-4">📤</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                                            {t('Actividad Ya Enviada', 'Activity Already Sent')}
                                        </h3>
                                        <p className="text-gray-600 mb-6">
                                            {t('Esta actividad ya fue enviada anteriormente y está siendo revisada.', 'This activity was already sent previously and is being reviewed.')}
                                        </p>
                                        <button
                                            onClick={handleBackToExperiments}
                                            className="px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            {t('Volver a Actividades', 'Back to Activities')}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            // Vista de selección de actividad
                            <div className="bg-white rounded-lg shadow p-8 flex flex-col items-center justify-center min-h-96">
                                <div className="text-center">
                                    <div className="text-6xl mb-6">🔬</div>
                                    <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('Selecciona una Actividad', 'Select an Activity')}</h2>
                                    <p className="text-lg text-gray-600">
                                        {t('Elige una actividad de la lista para ver los detalles y comenzar', 'Choose an activity from the list to see details and start')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Columna derecha: Descripción de actividades */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('Descripción de Actividades', 'Activity Description')}</h2>
                            
                            {selectedActivity ? (
                                <div>
                                    <h3 className="font-semibold text-gray-800 mb-3">
                                        {t('Actividad', 'Activity')} {selectedActivity.number}
                                    </h3>
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <p className="text-blue-800 break-words overflow-wrap-anywhere">
                                            {selectedActivity.description_es && selectedActivity.description_en
                                                ? (language === 'es' ? selectedActivity.description_es : selectedActivity.description_en)
                                                : selectedActivity.description || t("Descripción de la actividad no disponible.", "Activity description not available.")}
                                        </p>
                                    </div>
                                    <p className="text-gray-700 mt-4 font-medium">
                                        {t('Sigue los pasos de la actividad y documenta tus observaciones con una foto.', 'Follow the activity steps and document your observations with a photo.')}
                                    </p>
                                </div>
                            ) : currentView === 'experiments' ? (
                                <div className="text-center text-gray-500">
                                    <div className="text-4xl mb-3">🔬</div>
                                    <p>{t('Selecciona una actividad para ver su descripción', 'Select an activity to see its description')}</p>
                                </div>
                            ) : (
                                <div className="text-center text-gray-500">
                                    <div className="text-4xl mb-3">📚</div>
                                    <p>{t('Selecciona un libro para ver las actividades disponibles', 'Select a book to see available activities')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para código de producto */}
            {codeModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center mb-4">
                            <div className="text-2xl mr-2">🔒</div>
                            <h3 className="text-xl font-bold text-gray-800">
                                {t('Desbloquear Libro', 'Unlock Book')} {selectedBook}
                            </h3>
                        </div>
                        
                        <p className="text-gray-600 mb-4">
                            {t('Ingresa el código de producto que viene con tu Libro', 'Enter the product code that comes with your Book')} {selectedBook}:
                        </p>
                        
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={t('Ej: LIBRO1-2024', 'Ex: BOOK1-2024')}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                        />
                        
                        {codeError && (
                            <p className="text-red-600 text-sm mb-4">{codeError}</p>
                        )}
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setCodeModalOpen(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                            >
                                {t('Cancelar', 'Cancel')}
                            </button>
                            <button
                                onClick={handleUnlockBook}
                                disabled={unlocking}
                                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                            >
                                {unlocking ? t('Desbloqueando...', 'Unlocking...') : t('Desbloquear', 'Unlock')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de felicitación para actividades aprobadas */}
            {congratsModalOpen && selectedActivity && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    {/* Confeti animado */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {[...Array(20)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-bounce"
                                style={{
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                    animationDelay: `${Math.random() * 2}s`,
                                    animationDuration: `${2 + Math.random() * 2}s`
                                }}
                            />
                        ))}
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-8 max-w-lg w-full text-white text-center shadow-2xl relative">
                        <div className="text-8xl mb-6 animate-bounce">🎉</div>
                        <h3 className="text-3xl font-bold mb-6">{t('¡Felicitaciones!', 'Congratulations!')}</h3>
                        
                        {selectedBook && selectedActivity && (
                            <>
                                {(() => {
                                    const badge = selectedBook && selectedActivity.number ? getBadge(selectedBook, selectedActivity.number) : null;
                                    const badgeImage = selectedBook && selectedActivity.number ? getBadgeImage(selectedBook, selectedActivity.number) : '';
                                    const badgeName = selectedBook && selectedActivity.number ? getBadgeName(selectedBook, selectedActivity.number) : '';
                                    
                                    return badge ? (
                                        <div className="mb-6">
                                            {/* Mostrar la insignia real si está disponible */}
                                            {badgeImage && badgeImage !== '' ? (
                                                <div className="relative mb-4">
                                                    <img 
                                                        src={badgeImage} 
                                                        alt={`Insignia ${badgeName}`}
                                                        className="w-32 h-32 mx-auto rounded-full border-4 border-yellow-400 shadow-xl"
                                                    />
                                                    {/* Efecto de brillo dorado */}
                                                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-300/40 to-transparent rounded-full animate-ping"></div>
                                                </div>
                                            ) : (
                                                <div className="text-6xl mb-4">{badge.emoji}</div>
                                            )}
                                            <h4 className="text-2xl font-bold mb-3">{badge.name}</h4>
                                            <p className="text-lg opacity-90 mb-4">{badge.description}</p>
                                        </div>
                                    ) : (
                                        <div className="mb-6">
                                            {/* Mostrar la insignia real si está disponible */}
                                            {badgeImage && badgeImage !== '' ? (
                                                <div className="relative mb-4">
                                                    <img 
                                                        src={badgeImage} 
                                                        alt={`Insignia ${badgeName}`}
                                                        className="w-32 h-32 mx-auto rounded-full border-4 border-yellow-400 shadow-xl"
                                                    />
                                                    {/* Efecto de brillo dorado */}
                                                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-300/40 to-transparent rounded-full animate-ping"></div>
                                                </div>
                                            ) : (
                                                <img 
                                                    src="/images/badge.png" 
                                                    alt={t('Insignia Aprobada', 'Approved Badge')} 
                                                    className="w-20 h-20 mx-auto mb-4"
                                                />
                                            )}
                                            <h4 className="text-xl font-bold mb-3">
                                                {badgeImage && badgeImage !== '' ? badgeName : t('¡Actividad Aprobada!', 'Activity Approved!')}
                                            </h4>
                                        </div>
                                    );
                                })()}
                                
                                {/* Mensaje principal con fecha, insignia y actividad */}
                                <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-6">
                                    <p className="text-lg font-semibold mb-2">
                                        {t('Con fecha', 'As of')} {selectedActivity.reviewed_at ? formatDate(selectedActivity.reviewed_at) : t('reciente', 'recent')} {t('se te otorga la insignia', 'you are awarded the badge')}
                                    </p>
                                    {(() => {
                                        const badge = selectedBook && selectedActivity.number ? getBadge(selectedBook, selectedActivity.number) : null;
                                        const badgeName = selectedBook && selectedActivity.number ? getBadgeName(selectedBook, selectedActivity.number) : '';
                                        
                                        return badge ? (
                                            <p className="text-xl font-bold text-yellow-300">
                                                "{badge.name}"
                                            </p>
                                        ) : badgeName ? (
                                            <p className="text-xl font-bold text-yellow-300">
                                                "{badgeName}"
                                            </p>
                                        ) : (
                                            <p className="text-xl font-bold text-yellow-300">
                                                "{t('Científico Destacado', 'Outstanding Scientist')}"
                                            </p>
                                        );
                                    })()}
                                    <p className="text-lg font-semibold mt-2">
                                        {t('por cumplir con la', 'for completing the')} {selectedActivity.title}
                                    </p>
                                </div>
                            </>
                        )}
                        
                        <p className="text-sm opacity-75 mb-6">
                            {t('Has ganado una insignia por tu increíble trabajo científico. ¡Sigue explorando y descubriendo!', 'You have earned a badge for your incredible scientific work. Keep exploring and discovering!')}
                        </p>
                        
                        <button
                            onClick={() => setCongratsModalOpen(false)}
                            className="px-8 py-4 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 font-bold text-lg transition-all duration-200 hover:scale-105"
                        >
                            {t('¡Entendido!', 'Got it!')}
                        </button>
                    </div>
                </div>
            )}

            {/* Backdrop de carga */}
            {uploading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-gray-800">{t('Subiendo actividad...', 'Uploading activity...')}</span>
                    </div>
                </div>
            )}

            {/* Celebración de nuevo título desbloqueado */}
            <TitleUnlockCelebration
                isVisible={titleCelebration.isVisible}
                onClose={handleCloseTitleCelebration}
                bookNumber={titleCelebration.bookNumber}
                title={titleCelebration.title}
            />

            {/* Selector de avatares */}
            <AvatarSelector
                isVisible={showAvatarSelector}
                currentAvatarId={selectedAvatarId}
                onAvatarSelect={handleAvatarSelect}
                onClose={handleCloseAvatarSelector}
            />
        </div>
    );
};

export default ActivityPage;